import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useClub } from "@/contexts/ClubContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Target, Crosshair, Circle, Flame, Music, CalendarDays, ArrowRight, Tv, ShoppingBag, Download, Crown, Users, LogIn } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON_MAP = { Target, Crosshair, Circle, Flame, Music, Tv, ShoppingBag, Crown };

const CLUB_IMAGES = {
  "dsv-niners": "/assets/n5s222hc_AdobeStock_199311994.jpeg",
  "sdv-niners": "/assets/27zlz5a9_dart-bull-s-eye-close-up.jpg",
  "bsv-niners": "/assets/4ey7g8jx_billiard-balls-blue-pool-table.jpg",
  "perchten": "https://images.unsplash.com/photo-1764776810948-162695576412?w=600&q=80",
  "soundtheatre": "/assets/msov0jzw_Bild1.jpg",
  "sportuebertragungen": "/assets/6b9ag9sx_Leinwand_Live_Sport_04.jpg",
  "vip": "/api/images/vip-hero",
  "shop": "https://images.unsplash.com/photo-1556817411-31ae72fa3ea0?w=600&q=80",
};

export default function HomePage() {
  const { clubs, setActiveClub, getClubAccent } = useClub();
  usePageTitle(null);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events?featured=true`).then(r => setEvents(r.data.slice(0, 4))).catch(() => {});
    axios.get(`${API}/news`).then(r => setNews(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/sponsors`).then(r => setSponsors(r.data)).catch(() => {});
    axios.get(`${API}/checkin/status`).then(r => setCheckinStatus(r.data)).catch(() => {});
  }, []);

  const vereine = useMemo(() => clubs.filter(c => !c.isBar && !c.isSpecial), [clubs]);
  const activeCheckins = useMemo(
    () => (checkinStatus ? Object.entries(checkinStatus.per_club).filter(([, count]) => count > 0) : []),
    [checkinStatus]
  );

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] md:h-[80vh] overflow-hidden" data-testid="hero-section">
        <img src="/assets/xpx73wn5_15_mehr_Farbe.jpg"
          alt="Ninersbillard" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-8 pb-12 md:pb-20 max-w-6xl mx-auto">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-4"
              style={{ fontFamily: 'Oswald' }}>
              Niners<span style={{ color: '#fbbf24' }}>billard</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-xl mb-6">
              Deine Sportsbar im Herzen von Wels. Billard, Darts, Live-Musik und mehr.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/reservierung" data-testid="hero-reserve-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm hover:-translate-y-1 transition-transform duration-200">
                Jetzt reservieren <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      {events.length > 0 && (
        <section className="py-12 md:py-24 px-4 md:px-8" data-testid="featured-events-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
                Kommende <span style={{ color: '#fbbf24' }}>Events</span>
              </h2>
              <Link to="/kalender" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                Alle anzeigen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {events.map((event, i) => (
                <Link key={event.id} to={`/kalender?event=${event.id}`}
                  className={`bg-card border border-white/5 card-hover animate-fade-in-up stagger-${i + 1} block overflow-hidden`}
                  data-testid={`featured-event-${event.id}`}>
                  {event.image_url && (
                    <img src={resolveImageUrl(event.image_url)} alt={event.title}
                      className="w-full max-h-72 object-contain bg-black/30"
                      onError={e => { e.target.style.display = 'none'; }}
                      data-testid={`featured-event-img-${event.id}`} />
                  )}
                  <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <Badge style={{ backgroundColor: getClubAccent(event.club_id), color: '#09090b' }}
                      className="text-xs font-bold uppercase">
                      {clubs.find(c => c.id === event.club_id)?.name || event.club_id}
                    </Badge>
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>
                    {event.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{new Date(event.date).toLocaleDateString('de-AT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    {event.time && <span>{event.time} Uhr{event.end_time ? ` - ${event.end_time} Uhr` : ''}</span>}
                    <span
                      onClick={e => { e.preventDefault(); e.stopPropagation(); window.open(`${API}/events/${event.id}/ical`, '_blank'); }}
                      className="flex items-center gap-1 ml-auto text-primary hover:text-primary/80 transition-colors cursor-pointer"
                      data-testid={`home-ical-${event.id}`}>
                      <Download className="w-3 h-3" /> .ics
                    </span>
                  </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Check-in Status */}
      {checkinStatus && checkinStatus.total > 0 && (
        <section className="py-8 md:py-12 px-4 md:px-8 border-t border-white/5" data-testid="checkin-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Users className="w-5 h-5 text-green-500" />
              <h2 className="text-lg md:text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
                Gerade da: <span className="text-green-500">{checkinStatus.total}</span> {checkinStatus.total === 1 ? 'Mitglied' : 'Mitglieder'}
              </h2>
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            </div>
            <div className="flex flex-wrap gap-3">
              {activeCheckins.map(([cid, count]) => {
                const club = clubs.find(c => c.id === cid);
                if (!club) return null;
                return (
                  <div key={cid} className="flex items-center gap-2 px-4 py-2 bg-card border border-white/5" data-testid={`checkin-club-${cid}`}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: club.accent }} />
                    <span className="text-sm font-bold">{count}</span>
                    <span className="text-xs text-muted-foreground">{club.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Clubs */}
      <section className="py-12 md:py-24 px-4 md:px-8 border-t border-white/5" data-testid="clubs-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-8" style={{ fontFamily: 'Oswald' }}>
            Unsere <span style={{ color: '#fbbf24' }}>Vereine</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {vereine.map((club, i) => {
              const Icon = ICON_MAP[club.icon];
              return (
                <Link key={club.id} to={`/club/${club.id}`}
                  onClick={() => setActiveClub(club.id)}
                  data-testid={`club-card-${club.id}`}
                  className={`group relative overflow-hidden bg-card border border-white/5 card-hover animate-fade-in-up stagger-${i + 1}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={resolveImageUrl(CLUB_IMAGES[club.id]) || CLUB_IMAGES[club.id]} alt={club.name}
                      onError={e => { e.target.style.display = 'none'; }}
                      className="w-full h-full object-cover img-zoom opacity-70 group-hover:opacity-90" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-5 h-5" style={{ color: club.accent }} />
                      <span className="text-xs font-medium uppercase tracking-widest" style={{ color: club.accent }}>
                        {club.type}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: 'Oswald' }}>
                      {club.name}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest News */}
      {news.length > 0 && (
        <section className="py-12 md:py-24 px-4 md:px-8 border-t border-white/5" data-testid="news-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
                Neues aus dem <span style={{ color: '#fbbf24' }}>Ninersbillard</span>
              </h2>
              <Link to="/neuigkeiten" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                Alle anzeigen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {news.map((item, i) => (
                <div key={item.id} className={`bg-card border border-white/5 p-6 card-hover animate-fade-in-up stagger-${i + 1}`}
                  data-testid={`news-preview-${item.id}`}>
                  <Badge style={{ backgroundColor: getClubAccent(item.club_id), color: '#09090b' }}
                    className="text-xs font-bold uppercase mb-3">
                    {clubs.find(c => c.id === item.club_id)?.name || item.club_id}
                  </Badge>
                  <h3 className="text-lg font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-3">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Info */}
      <section className="py-12 md:py-24 px-4 md:px-8 border-t border-white/5" data-testid="info-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-card border border-white/5 p-6">
            <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Ausstattung</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>750 Quadratmeter</li>
              <li>10 Billard-Tische</li>
              <li>12 eDart-Automaten</li>
              <li>2 Steeldart-Boards</li>
              <li>Live-Musik Bühne</li>
              <li>30m² Leinwand & 8 Screens</li>
              <li>Niners-Shop (Dart & Billard Zubehör)</li>
            </ul>
          </div>
          <div className="bg-card border border-white/5 p-6">
            <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Öffnungszeiten</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>Mo - Do: 16:00 - 24:00</li>
              <li>Fr - Sa: 13:00 - 02:00</li>
              <li>So: 13:00 - 24:00</li>
            </ul>
          </div>
          <div className="bg-card border border-white/5 p-6">
            <h3 className="text-lg font-bold uppercase tracking-tight mb-3" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Kontakt</h3>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>Ninersbillard</li>
              <li>Stelzhamerstraße 9, 4600 Wels</li>
              <li className="pt-1"><a href="tel:+437242216406" className="hover:text-foreground">Tel: +43 (0)7242 216406</a></li>
              <li><a href="mailto:office@ninersbillard.com" className="hover:text-foreground">office@ninersbillard.com</a></li>
              <li className="pt-1">
                <a href="https://www.facebook.com/ninersbillard/" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="facebook-link">
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Anfahrtsplan */}
        <div className="max-w-6xl mx-auto mt-8">
          <div className="bg-card border border-white/5 p-6" data-testid="anfahrt-section">
            <h3 className="text-lg font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald', color: '#fbbf24' }}>Anfahrt</h3>
            <div className="w-full aspect-video md:aspect-[21/9]">
              <iframe
                src="https://maps.google.com/maps?q=Niners+-+Die+Sportsbar+im+Zentrum+von+Wels&z=16&ie=UTF8&output=embed"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                referrerPolicy="no-referrer-when-downgrade" title="Anfahrt Ninersbillard"
                className="w-full h-full"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-3">Stelzhamerstraße 9, 4600 Wels</p>
          </div>
        </div>

        {/* Sponsoren unserer Vereine */}
        <div className="max-w-6xl mx-auto mt-8" data-testid="sponsors-section">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald' }}>Sponsoren unserer <span style={{ color: '#fbbf24' }}>Vereine</span></h2>
          <p className="text-sm text-muted-foreground mb-6">
            Wir möchten uns recht herzlich bei unseren Sponsoren bedanken die uns mit Rat, Tat, Material, Leistungen für die Mitglieder und auch monetären Zuwendungen unterstützen!
          </p>
          {sponsors.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-4">
              {sponsors.slice(0, 6).map(s => (
                <a key={s.id} href={s.link || "#"} target={s.link ? "_blank" : "_self"} rel="noopener noreferrer"
                  className="group flex items-center justify-center bg-white border border-white/10 hover:border-primary/40 p-4 rounded transition-all duration-300 hover:-translate-y-1"
                  style={{ width: '160px', height: '80px' }}
                  data-testid={`sponsor-logo-${s.id}`}>
                  <img
                    src={s.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}` : s.logo_url}
                    alt={s.name}
                    className="max-h-full max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </a>
              ))}
            </div>
          )}
          <Link to="/sponsors" className="text-sm text-primary hover:underline" data-testid="sponsors-page-link">
            Alle Sponsoren ansehen &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
}
